import useSWR from 'swr';
import { fetchEndpointData } from '@/utils/fetch.client';

export interface SystemSetting {
  key: string;
  /** The stored value — or, when `is_secret`, a mask like `••••••••1a2b`. */
  value: string;
  /** Secret settings are write-only: the server never returns the credential. */
  is_secret: boolean;
  updated_at: string;
  updated_by: string | null;
}

interface ListSystemSettingsResponse {
  settings: SystemSetting[];
}

export const SETTING_DAYTONA_SNAPSHOT_NAME = 'daytona_snapshot_name';
export const SETTING_POSTMARK_SERVER_TOKEN = 'postmark_server_token';
export const SETTING_EMAIL_FROM_ADDRESS = 'email_from_address';

/**
 * Gross margin taken off a licence price to reach its included usage allowance,
 * as an integer percent. Applied **live**, so changing it re-prices every space's
 * open period at once.
 */
export const SETTING_LICENSE_MARGIN_PERCENT = 'license_margin_percent';

/**
 * Master switch for licence/credit enforcement. Absent means **off** — balances
 * are still computed and shown, but nothing is refused.
 */
export const SETTING_CREDIT_ENFORCEMENT_ENABLED = 'credit_enforcement_enabled';

export function useSystemSettings(enabled: boolean) {
  const { data, mutate, isLoading, error } = useSWR(enabled ? '/api/v1/admin/system-settings' : null, () =>
    fetchEndpointData<ListSystemSettingsResponse>('/api/v1/admin/system-settings'),
  );
  return { settings: data?.settings ?? [], mutate, isLoading, error };
}

export async function updateSystemSetting(key: string, value: string) {
  return fetchEndpointData<SystemSetting>(`/api/v1/admin/system-settings/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: { value },
  });
}

export async function deleteSystemSetting(key: string) {
  return fetchEndpointData<void>(`/api/v1/admin/system-settings/${encodeURIComponent(key)}`, {
    method: 'DELETE',
  });
}

/**
 * The email configuration the server would actually use for the next send.
 *
 * Distinct from the raw settings list because the values fall back to env vars —
 * this is what lets the form show a correct default with nothing saved, and show
 * which rung is live before you overwrite it.
 */
export interface EmailConfig {
  from_address: string;
  from_address_source: 'setting' | 'env';
  /** A hint at the active token, or null when email is off entirely. */
  token_masked: string | null;
  token_source: 'setting' | 'env' | 'none';
}

export function useEmailConfig(enabled: boolean) {
  const { data, mutate, isLoading, error } = useSWR(enabled ? '/api/v1/admin/email/config' : null, () =>
    fetchEndpointData<EmailConfig>('/api/v1/admin/email/config'),
  );
  return { config: data, mutate, isLoading, error };
}

/** Sends a test message to the calling admin's own address. */
export async function sendTestEmail() {
  return fetchEndpointData<{ sent_to: string }>('/api/v1/admin/email/test', { method: 'POST' });
}
